import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, Check } from 'lucide-react';
import PublicHeader from '../components/common/PublicHeader';
import subscriptionService from '../services/subscriptionService';

const Pricing = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [billingInterval, setBillingInterval] = useState('monthly');
  const navigate = useNavigate();

  useEffect(() => {
    const loadPlans = async () => {
      try {
        const res = await subscriptionService.getPlans();
        setPlans(res.data);
      } catch (err) {
        console.error('Error loading plans:', err);
      } finally {
        setLoading(false);
      }
    };
    loadPlans();
  }, []);

  const handleGetStarted = (plan) => {
    navigate(`/register?plan=${plan.tier}&interval=${billingInterval}`);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <PublicHeader />

      {/* Hero */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 py-16 text-center">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Simple pricing for your practice
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Full-featured practice management with a 30-day free trial. No credit card required to start.
          </p>

          {/* Interval toggle */}
          <div className="mt-8 inline-flex items-center bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setBillingInterval('monthly')}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-all ${
                billingInterval === 'monthly'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingInterval('annual')}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-all ${
                billingInterval === 'annual'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Annual
              <span className="ml-1.5 text-xs font-medium text-green-600">Save 20%</span>
            </button>
          </div>
        </div>
      </div>

      {/* Plans */}
      <div className="max-w-4xl mx-auto px-4 py-12">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-6 h-6 animate-spin text-yellow-500" />
            <span className="ml-2 text-gray-600">Loading plans...</span>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {plans.map((plan, index) => (
              <div
                key={plan.id}
                className={`relative bg-white rounded-2xl border-2 p-8 shadow-sm transition-all hover:shadow-md ${
                  index === plans.length - 1
                    ? 'border-yellow-400'
                    : 'border-gray-200'
                }`}
              >
                {index === plans.length - 1 && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="bg-yellow-500 text-white text-xs font-medium px-3 py-1 rounded-full">
                      Most Popular
                    </span>
                  </div>
                )}

                <div className="text-center mb-6">
                  <h3 className="text-xl font-bold text-gray-900">{plan.name}</h3>
                  <p className="text-sm text-gray-500 mt-2">{plan.description}</p>
                </div>

                {/* Pricing */}
                <div className="text-center mb-8">
                  {billingInterval === 'monthly' ? (
                    <>
                      <div className="flex items-baseline justify-center">
                        <span className="text-4xl font-bold text-gray-900">
                          ${Number(plan.price_monthly).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </span>
                        <span className="text-gray-500 ml-1">/mo</span>
                      </div>
                      {Number(plan.price_annual) > 0 && (
                        <p className="text-sm text-gray-400 mt-1">
                          or ${Number(plan.price_annual).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}/yr (save ${(Number(plan.price_monthly) * 12 - Number(plan.price_annual)).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })})
                        </p>
                      )}
                    </>
                  ) : (
                    <>
                      <div className="flex items-baseline justify-center">
                        <span className="text-4xl font-bold text-gray-900">
                          ${(Number(plan.price_annual) / 12).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </span>
                        <span className="text-gray-500 ml-1">/mo</span>
                      </div>
                      <p className="text-sm text-green-600 font-medium mt-1">
                        ${Number(plan.price_annual).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}/yr &mdash; save ${(Number(plan.price_monthly) * 12 - Number(plan.price_annual)).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}/yr
                      </p>
                    </>
                  )}
                </div>

                {/* Features list */}
                <ul className="space-y-3 mb-8">
                  {[
                    `Up to ${plan.max_cases ? plan.max_cases.toLocaleString() : 'unlimited'} active cases`,
                    plan.max_users ? `Up to ${plan.max_users} team members` : 'Unlimited team members',
                    'Full practice management suite',
                    'Calendar with Google & Outlook sync',
                    'Client & case management',
                    'Invoicing & trust accounting',
                    'Document storage & e-signatures',
                    'AI-powered case analysis',
                    'SMS & email communications',
                    'Reports & dashboards',
                    '30-day free trial',
                  ].map((feature, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
                      <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => handleGetStarted(plan)}
                  className={`w-full py-3 px-4 text-sm font-medium rounded-lg transition-all ${
                    index === plans.length - 1
                      ? 'bg-yellow-500 text-white hover:bg-yellow-600'
                      : 'bg-gray-900 text-white hover:bg-gray-800'
                  }`}
                >
                  Get Started Free
                </button>
                <p className="text-xs text-gray-400 text-center mt-2">
                  30-day free trial, cancel anytime
                </p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="border-t border-gray-200 bg-white">
        <div className="max-w-4xl mx-auto px-4 py-8 text-center text-sm text-gray-500">
          Already have an account?{' '}
          <a href="/login" className="text-yellow-600 hover:text-yellow-700 font-medium">
            Log in
          </a>
        </div>
      </div>
    </div>
  );
};

export default Pricing;
